import React, { useCallback, useMemo } from 'react';
import {
  ArrayLayoutProps,
  ArrayTranslations,
  composePaths,
  createDefaultValue,
  findUISchema,
  type Layout,
} from '@jsonforms/core';
import {
  JsonFormsDispatch,
  withArrayTranslationProps,
  withJsonFormsArrayLayoutProps,
  withTranslateProps,
} from '@jsonforms/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// @ts-expect-error lodash submodules lack type declarations
import range from 'lodash/range';
// @ts-expect-error lodash submodules lack type declarations
import merge from 'lodash/merge';

function CardArrayRendererComponent(
  props: ArrayLayoutProps & { translations: ArrayTranslations },
) {
  const {
    enabled,
    data,
    path,
    schema,
    uischema,
    addItem,
    removeItems,
    renderers,
    cells,
    label,
    required,
    rootSchema,
    config,
    uischemas,
    visible,
    translations,
  } = props;

  const appliedOptions = merge({}, config, uischema.options);

  const innerCreateDefaultValue = useCallback(
    () => createDefaultValue(schema, rootSchema),
    [schema, rootSchema],
  );

  // Build a stable UI schema with sorted controls so field order is
  // deterministic regardless of Firestore property ordering.
  // Note: `schema` here is already the *item* schema (type: object).
  const stableItemUISchema = useMemo((): Layout | undefined => {
    if (!schema.properties) return undefined;
    return {
      type: 'VerticalLayout',
      elements: Object.keys(schema.properties)
        .sort((a, b) => a.localeCompare(b))
        .map((key) => ({
          type: 'Control' as const,
          scope: `#/properties/${key}`,
        })),
    };
  }, [schema]);

  if (!visible) return null;

  const itemCount = data ?? 0;

  return (
    <Box sx={{ my: 1 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 500, color: 'text.primary' }}
        >
          {label}
          {required ? ' *' : ''}
        </Typography>
        {enabled && !appliedOptions.disableAdd && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addItem(path, innerCreateDefaultValue())}
            sx={{ textTransform: 'none' }}
          >
            {translations.addTooltip || 'Add'}
          </Button>
        )}
      </Stack>

      {/* Items */}
      {itemCount > 0 ? (
        <Stack spacing={1.5}>
          {range(itemCount).map((index: number) => {
            const childPath = composePaths(path, `${index}`);
            const foundUISchema =
              stableItemUISchema ??
              findUISchema(
                uischemas ?? [],
                schema,
                uischema.scope,
                path,
                undefined,
                uischema,
                rootSchema,
              );

            return (
              <Box
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 2,
                  pt: 1,
                  position: 'relative',
                  backgroundColor: 'action.hover',
                }}
              >
                {/* Card header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 500 }}
                  >
                    #{index + 1}
                  </Typography>
                  {enabled &&
                    removeItems &&
                    !appliedOptions.disableRemove && (
                      <IconButton
                        size="small"
                        onClick={removeItems(path, [index])}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                </Stack>

                {/* Item fields */}
                <JsonFormsDispatch
                  schema={schema}
                  uischema={foundUISchema}
                  path={childPath}
                  renderers={renderers}
                  cells={cells}
                  enabled={enabled}
                />
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}
        >
          {translations.noDataMessage || 'No items'}
        </Typography>
      )}
    </Box>
  );
}

export const CardArrayRenderer = withJsonFormsArrayLayoutProps(
  withTranslateProps(
    withArrayTranslationProps(CardArrayRendererComponent),
  ),
);
